import { Wrapper } from './style';
import { unstable_usePrompt, useNavigate, useParams } from 'react-router-dom';
import {
  useGetAutomationFileMappings,
  useGetAutomationFundMappings
} from '../../../services/automation.service';
import { useEffect, useMemo, useRef, useState } from 'react';
import WarningIcon from '../../../assets/icons/warning-grey-outlined.svg?react';
import {
  useGetFilesByPeriodId,
  useGetFundsByPeriodId,
  useUploadFundFiles
} from '../../../services/funds.service';
import { FundCard } from './components/FundCard';
import { FiUpload } from 'react-icons/fi';
import { useGetWorkflowById } from '../../../services/workflow.services';
import { usePeriodById, usePeriodFiles } from '../../../services/period.service';
import usePromptUnloadPage from '../../../hooks/usePromptUnloadPage';
import useAutomationFiles, {
  normalizeAutomationFundMappings,
  normalizeInitialSetupFundMappings
} from './hooks/useAutomationFiles';
import useMutateAutomationFiles from './hooks/useMutateAutomationFiles';
import TableHeader from './components/TableHeader';
import useDetectProtectedFiles from '../../../hooks/useDetectProtectedFiles';
import { SkipPasswordModal } from '../Funds/components/SkipPasswordModal';
import { WarningPopup } from '../../../components/WarningPopup';
import toast from 'react-hot-toast';
import reportStore from './store';
import { useUserGroupsCurrentUserAdminState } from '../../../services/user.service';
import { checkIsLockedAutomation } from '../../../utils/lockAutomation';
import agentStore from '../../../store/agent';
import { WEB_PAGE_STATE_ENUMS } from '../../../components/ChatWidget/constants';
import { useFeatureFlag, FEATURE_FLAG_NAMES } from '../../../hooks/useFeatureFlag';
import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { DropZone } from '../../../components/DropZone';
import { getWorkflowDropzoneAccept, getWorkflowFileAccept } from '../../../consts/fileTypes';
import { REPORT_NAMINGS, REPORT_TYPES } from '../../../consts/reportTypes';
import { detectDuplicateFiles, extractExistingFiles } from './utils/duplicateDetector';
import { DuplicateFilesModal } from '../../../components/DuplicateFilesModal';
import { NewDeleteConfirmModal } from '../../../components/NewDeleteConfirmModal';
import { PermissionGuard } from '../../../components/PermissionGuard';
import { permissionPolicies } from '../../../permissions/policies';
import {
  useCreateConnectSession,
  buildConnectUiUrl,
  useImportCloudFiles,
  useIntegrationConnections,
  useIntegrationFiles
} from '../../../services/integrations.service';
import AddFilesSourceModal from '../components/AddFilesSourceModal';
import CloudImportModal from '../components/CloudImportModal';
import {
  getConnectedProviderKeys,
  getPreviewIntegrationCards,
  getProviderConnections
} from '../../../consts/integrationsCatalog';
import { useWorkflowChrome } from '../../../components/Layout/WorkflowChromeContext';
import Breadcrumb from '../../../components/Breadcrumb/index';
import { SIGNALR_LISTENERS } from '../../../config/signalr';
import { useSignalRListener } from '../../../hooks/useSignalR';
import { validateUploadFiles } from './utils/uploadValidation';

function hasInProgressFile(data) {
  const result = JSON.stringify(data).includes('in progress');

  return result;
}

export default function UploadFiles() {
  const { periodId, id } = useParams();

  const ref = useRef();
  const navigate = useNavigate();

  const [fundMappings, setFundMappings] = useState([]);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [hasInProgressFiles, setHasInProgressFiles] = useState(false);
  const [isNextButtonClicked, setIsNextButtonClicked] = useState(false);
  const [duplicateFiles, setDuplicateFiles] = useState([]);
  const [pendingUploadFiles, setPendingUploadFiles] = useState(null);
  const [isCloudModalOpen, setIsCloudModalOpen] = useState(false);
  const [isAddFilesModalOpen, setIsAddFilesModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedConnectionId, setSelectedConnectionId] = useState('');
  const [selectedCloudFiles, setSelectedCloudFiles] = useState([]);
  const [cloudQuery, setCloudQuery] = useState('');
  const [cloudPath, setCloudPath] = useState('');
  const [cloudFolderId, setCloudFolderId] = useState('');
  const [cloudFolderIdTrail, setCloudFolderIdTrail] = useState([]);
  const [cloudFolderTrail, setCloudFolderTrail] = useState([]);
  const recentlyUploadedFilesRef = useRef([]);

  const providers = useMemo(() => getPreviewIntegrationCards(), []);

  const { data: quarters } = useGetWorkflowById({
    id,
    queryParams: {
      enabled: !!id
    }
  });

  const uploadFundFiles = useUploadFundFiles();
  const createConnectSession = useCreateConnectSession();
  const importCloudFiles = useImportCloudFiles();

  const {
    data: connectionsData,
    refetch: refetchConnections,
    isFetching: isFetchingConnections
  } = useIntegrationConnections({
    queryOptions: {
      enabled: isCloudModalOpen
    }
  });

  const allConnections = useMemo(() => connectionsData?.connections || [], [connectionsData]);
  const selectedProviderConnections = useMemo(
    () => getProviderConnections(allConnections, selectedProvider),
    [allConnections, selectedProvider]
  );
  const connectedProviders = useMemo(
    () =>
      providers.filter(provider => getConnectedProviderKeys(allConnections).includes(provider.key)),
    [allConnections, providers]
  );

  const {
    data: cloudFilesData,
    refetch: refetchCloudFiles,
    isFetching: isFetchingCloudFiles
  } = useIntegrationFiles({
    params:
      selectedProvider === 'google-drive' || selectedProvider === 'sharepoint-online'
        ? {
            provider_key: selectedProvider,
            connection_id: selectedConnectionId,
            query: cloudQuery || undefined,
            folder_id: cloudFolderId || undefined
          }
        : {
            provider_key: selectedProvider,
            connection_id: selectedConnectionId,
            query: cloudQuery || undefined,
            path: cloudPath || undefined
          },
    queryOptions: {
      enabled: !!selectedProvider && !!selectedConnectionId && isCloudModalOpen
    }
  });

  const { data: period } = usePeriodById({ periodId });
  const { data: currentUser } = useUserGroupsCurrentUserAdminState();

  const { isLockedPeriod, setIsLockedPeriod } = reportStore;

  const canManageWorkflow = permissionPolicies.checkAccess({
    currentUserRole: currentUser?.role,
    currentUserId: currentUser?.azure_object_id,
    assignedTo: quarters?.assigned_to
  });

  const isInitialSetup = !period?.previous_period_id;
  const isCustomReportType = quarters?.report_type === REPORT_TYPES.CUSTOM;
  // Per-report-type plural label for funds — "AIFs" for Annex IV / Conversion,
  // "Funds" for Form PF, "Assets" for Custom. Falls back to "AIFs" until the
  // workflow record loads. Used to render the right noun on the
  // "Add Files For All …" actions + the bulk-upload modal title.
  const fundsPluralLabel = `${REPORT_NAMINGS.FUND?.[quarters?.report_type] || 'AIF'}s`;
  const isLastPeriod = quarters?.periods?.[0]?.id === period?.id;
  const acceptedFileTypes = getWorkflowFileAccept(quarters?.report_type);
  const acceptedDropzoneTypes = getWorkflowDropzoneAccept(quarters?.report_type);

  useEffect(() => {
    if (!period || !currentUser) return;

    setIsLockedPeriod(
      checkIsLockedAutomation(period, currentUser) ||
        (!isLastPeriod && currentUser?.role !== 'Admin')
    );
  }, [period, currentUser, isLastPeriod]);

  const { refetch: refetchInitialSetup, isFetching: isFetchingInitialSetup } =
    useGetFundsByPeriodId({
      periodId,
      queryParams: {
        // Poll while any file is still being processed, then stop. Mirrors
        // the automation hooks below. Without this, scratch / initial-setup
        // periods never refresh after upload — the file stayed stuck at
        // "in progress" in the UI until the user navigated away and back.
        refetchInterval: hasInProgressFiles ? 5000 : false,
        onSuccess: res => {
          handleProtectedFiles(res);
          if (isInitialSetup) {
            setFundMappings(normalizeInitialSetupFundMappings(res));
            setHasInProgressFiles(hasInProgressFile(res));
          }
        }
      }
    });

  const {
    hasChanges,
    isAllExpanded,
    expandedFileIds,
    openPopup,
    handleSelectSource,
    handleAddMappedSheet,
    handleRemoveMappedSheet,
    handleDeleteSheet,
    handleExpandFile,
    handleExpandAll,
    onChooseSheet,
    handleTrackChanges,
    setHasChanges,
    handleUnmapFile,
    handleAutoMatchSheets
  } = useAutomationFiles({
    fundMappings,
    onChangeFundMappings: setFundMappings
  });

  const { data: previousFilesAndSheets } = usePeriodFiles({
    periodId: period?.previous_period_id,
    params: {
      only_sheets_with_extractions: true
    },
    queryOptions: {
      enabled: !!period?.previous_period_id
    }
  });

  const {
    refetch: refetchAutomationFileMappings,
    isFetching: isFetchingAutomationFileMappings,
    isLoading: isLoadingAutomationFileMappings
  } = useGetAutomationFileMappings({
    periodId,
    queryParams: {
      refetchInterval: hasInProgressFiles ? 5000 : false,
      enabled: !!periodId && !isInitialSetup && isCustomReportType,
      onSuccess: res => {
        const payload = {
          not_mapped_previus_input_funds: [],
          not_mapped_current_input_funds: [],
          mapped_input_funds: [
            {
              id: null,
              previous_fund: {
                id: 1,
                name: 'Example 1',
                created_at: null,
                modified_at: null,
                not_mapped_per_input_files: []
              },
              current_fund: {
                id: 1,
                name: 'Example 1',
                created_at: null,
                modified_at: null,
                not_mapped_per_input_files: []
              },
              mapped_per_input_files: [],
              mapped_bulk_upload_files: {
                ...res
              }
            }
          ]
        };

        setHasChanges(false);
        setFundMappings(normalizeAutomationFundMappings(payload));
        setHasInProgressFiles(hasInProgressFile(payload));
      }
    }
  });

  const {
    refetch: refetchAutomationFundMappings,
    isFetching: isFetchingAutomationFundMappings,
    isLoading: isLoadingAutomationFundMappings
  } = useGetAutomationFundMappings({
    periodId,
    queryParams: {
      refetchInterval: hasInProgressFiles ? 5000 : false,
      enabled: !!periodId && !isInitialSetup && !isCustomReportType,
      onSuccess: res => {
        setHasChanges(false);
        if (!isCustomReportType) setFundMappings(normalizeAutomationFundMappings(res));
        if (!isCustomReportType) setHasInProgressFiles(hasInProgressFile(res));
      }
    }
  });

  const { refetch: refetchFiles } = useGetFilesByPeriodId({
    periodId,
    queryParams: {
      enabled: !!periodId && !!isCustomReportType,
      onSuccess: res => {
        handleProtectedPerFiles(res);
      }
    }
  });

  const refetch = () => {
    refetchFiles();

    // Scratch / initial-setup periods are served by useGetFundsByPeriodId
    // (refetchInitialSetup). The automation hooks below are gated on
    // `!isInitialSetup` for the initial fetch, but `refetch()` ignores the
    // `enabled` flag and would force a fetch — its onSuccess then overwrites
    // the scratch-shaped fundMappings AND sets `hasInProgressFiles = true`
    // from the automation response, which propagated as a global loading
    // spinner across every file row. Skip the automation refetches when
    // initial setup.
    if (isInitialSetup) {
      refetchInitialSetup();
      return;
    }

    if (!isCustomReportType) {
      refetchAutomationFundMappings();
    } else {
      refetchAutomationFileMappings();
    }
  };

  useSignalRListener(SIGNALR_LISTENERS.ON_FILE_PROCESSING_UPDATED, data => {
    console.log('SignalR ON_FILE_PROCESSING_UPDATED', data);
    if (data?.period_id === periodId) {
      refetchFiles();
    }
  });

  const {
    isModalOpen,
    fileName,
    currentFileNumber,
    totalFiles,
    isLoading,
    isSkipping,
    isDeletingAllProtectedFiles,
    onSkipFile,
    onSkipAllFiles,
    onUnblockSkipFile,
    handleProtectedFiles,
    handleProtectedPerFiles
  } = useDetectProtectedFiles({
    refetch: () => {
      refetch();
    },
    periodId
  });

  const isFetching =
    isFetchingInitialSetup || isFetchingAutomationFundMappings || isFetchingAutomationFileMappings;
  const isLoadingData = isLoadingAutomationFileMappings || isLoadingAutomationFundMappings;

  const {
    onChooseFile,
    handleUploadFundFiles,
    handleDeleteFile,
    onUploadFilesForAllFunds,
    handleDeletePeriodSheet,
    handleReUploadFundFile,
    onSave,
    handleDownloadFile,
    closeWarning,
    openWarning,
    handlePreventReset,
    isSaveMappingsLoading,
    isOpenWarning,
    isDeletingFile
  } = useMutateAutomationFiles({
    fundMappings,
    refetch,
    periodId,
    hasChanges,
    openPopup,
    onSaveSuccess: () => navigate(`/project/quaters/${id}`)
  });

  const onDeleteFile = (fileId, fileName) => {
    setSelectedFileId(fileId);
    setSelectedFileName(fileName);
  };

  const submitDeleteFile = resetConfirmInput => {
    handleDeleteFile(selectedFileId, () => {
      setSelectedFileId(null);
      setSelectedFileName('');
      resetConfirmInput?.();
    });
  };

  // Clear local cache whenever fundMappings updates (refetch completed with fresh server data)
  useEffect(() => {
    recentlyUploadedFilesRef.current = [];
  }, [fundMappings]);

  // Merge server-known files with locally tracked ones uploaded in this session
  // so detection works even while a post-upload refetch is still in progress
  const getFileKey = file => `${file.name}::${file.size}`;

  const getAllKnownFiles = () => {
    const serverFiles = extractExistingFiles(fundMappings);
    const serverFileKeys = new Set(serverFiles.map(getFileKey));
    const localOnly = recentlyUploadedFilesRef.current.filter(
      file => !serverFileKeys.has(getFileKey(file))
    );
    return [...serverFiles, ...localOnly];
  };

  const trackUploadedFiles = files => {
    files.forEach(f => recentlyUploadedFilesRef.current.push({ name: f.name, size: f.size }));
  };

  const handleUploadFundFilesWithDuplicateCheck = (e, fundId, workflowId) => {
    if (!e.target?.files) return;

    const validationError = validateUploadFiles(e.target.files);

    if (validationError) {
      toast.error(validationError);
      e.target.value = '';
      return;
    }

    const filesArray = Array.from(e.target.files);
    const existingFiles = getAllKnownFiles();

    if (existingFiles.length > 0) {
      const duplicates = detectDuplicateFiles(filesArray, existingFiles);
      if (duplicates.length > 0) {
        setDuplicateFiles(duplicates);
        setPendingUploadFiles({ files: filesArray, fundId, workflowId });
        if (e.target) e.target.value = '';
        return;
      }
    }

    trackUploadedFiles(filesArray);
    handleUploadFundFiles(e, fundId, workflowId);
  };

  const handleCloseDuplicateModal = () => {
    setDuplicateFiles([]);
    setPendingUploadFiles(null);
  };

  const openCloudModal = () => {
    setIsCloudModalOpen(true);
    setSelectedCloudFiles([]);
    setCloudPath('');
    setCloudFolderId('');
    setCloudFolderIdTrail([]);
    setCloudFolderTrail([]);
  };

  const closeCloudModal = () => {
    setIsCloudModalOpen(false);
    setSelectedCloudFiles([]);
    setCloudQuery('');
    setCloudPath('');
    setCloudFolderId('');
    setCloudFolderIdTrail([]);
    setCloudFolderTrail([]);
  };

  const openAddFilesModal = () => {
    setIsAddFilesModalOpen(true);
  };

  const closeAddFilesModal = () => {
    setIsAddFilesModalOpen(false);
  };

  const handleAddFilesFromLocal = () => {
    closeAddFilesModal();
    if (perFundPickerRef.current) {
      perFundPickerRef.current();
      perFundPickerRef.current = null;
    } else {
      onOpenFileUpload();
    }
  };

  const handleAddFilesFromIntegration = () => {
    closeAddFilesModal();
    openCloudModal();
  };

  const prevConnectionIdsRef = useRef(null);
  const perFundPickerRef = useRef(null);

  const handlePerFundUploadFlow = ({ openLocalPicker }) => {
    perFundPickerRef.current = openLocalPicker;
    openAddFilesModal();
  };

  const handleConnectProvider = async providerKey => {
    try {
      const response = await createConnectSession.mutateAsync({
        allowed_integrations: [providerKey]
      });
      const connectLink = buildConnectUiUrl(response);

      prevConnectionIdsRef.current = new Set(allConnections.map(c => c.connection_id));

      if (connectLink) {
        window.open(connectLink, '_blank', 'noopener,noreferrer');
      }
      setSelectedProvider('');
      setSelectedConnectionId('');
      setSelectedCloudFiles([]);
      setCloudPath('');
    } catch (err) {
      toast.error('Failed to create connect session');
    }
  };

  const handleRefreshConnections = async () => {
    try {
      await refetchConnections();
    } catch (err) {
      toast.error('Failed to refresh connections');
    }
  };

  useEffect(() => {
    if (!isCloudModalOpen) return;

    const refreshAndAutoSelect = async () => {
      const result = await refetchConnections();
      if (prevConnectionIdsRef.current !== null) {
        const newConnections = result.data?.connections || [];
        const newConn = newConnections.find(
          c => !prevConnectionIdsRef.current.has(c.connection_id)
        );
        if (newConn) {
          setSelectedConnectionId(newConn.connection_id);
          prevConnectionIdsRef.current = null;
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshAndAutoSelect();
      }
    };

    window.addEventListener('focus', refreshAndAutoSelect);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', refreshAndAutoSelect);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isCloudModalOpen, refetchConnections]);

  const toggleCloudFile = file => {
    setSelectedCloudFiles(prev => {
      const exists = prev.some(item => item.id === file.id);
      if (exists) return prev.filter(item => item.id !== file.id);
      return [...prev, file];
    });
  };

  const normalizeCloudPath = path => {
    if (!path) return '';

    let normalizedPath = path.trim();

    if (normalizedPath.includes('/drive/root:')) {
      normalizedPath = normalizedPath.split('/drive/root:', 2)[1];
    } else if (normalizedPath.startsWith('drive/root:')) {
      normalizedPath = normalizedPath.split('drive/root:', 2)[1];
    }

    if (normalizedPath.endsWith(':')) {
      normalizedPath = normalizedPath.slice(0, -1);
    }

    if (!normalizedPath || normalizedPath === '/') return '';

    return normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`;
  };

  const getParentCloudPath = path => {
    const segments = normalizeCloudPath(path).split('/').filter(Boolean);

    if (segments.length <= 1) return '';

    return `/${segments.slice(0, -1).join('/')}`;
  };

  const isCloudFolderNavigationEnabled = true;

  const visibleCloudFiles = useMemo(() => {
    const items = cloudFilesData?.items || [];
    const normalizedQuery = cloudQuery.trim().toLowerCase();

    if (!normalizedQuery) return items;

    return items.filter(file => file?.name?.toLowerCase().includes(normalizedQuery));
  }, [cloudFilesData?.items, cloudQuery]);

  const handleCloudItemClick = file => {
    if (file?.is_folder && isCloudFolderNavigationEnabled) {
      if (selectedProvider === 'google-drive' || selectedProvider === 'sharepoint-online') {
        setCloudFolderId(file.id);
        setCloudFolderIdTrail(prev => [...prev, file.id].filter(Boolean));
        setCloudFolderTrail(prev => [...prev, file.name].filter(Boolean));
        setCloudPath(prev =>
          [...prev.split('/').filter(Boolean), file.name].filter(Boolean).join('/')
        );
      } else {
        setCloudPath(prev => {
          const nextPath = `${prev || ''}/${file?.name || ''}`.replace(/\/+/g, '/');
          return nextPath;
        });
      }

      setSelectedCloudFiles([]);
      setCloudQuery('');
      return;
    }

    if (file?.is_folder) return;

    toggleCloudFile(file);
  };

  const handleCloudBack = () => {
    if (selectedProvider === 'google-drive' || selectedProvider === 'sharepoint-online') {
      setCloudFolderTrail(prev => prev.slice(0, -1));
      setCloudFolderIdTrail(prev => {
        const nextTrail = prev.slice(0, -1);
        setCloudFolderId(nextTrail[nextTrail.length - 1] || '');
        return nextTrail;
      });
    }

    setCloudPath(prev => getParentCloudPath(prev));
    setSelectedCloudFiles([]);
    setCloudQuery('');
  };

  const handleImportCloudFiles = async () => {
    if (!selectedConnectionId || selectedCloudFiles.length === 0) {
      toast.error('Select connection and at least one file');
      return;
    }

    try {
      await importCloudFiles.mutateAsync({
        periodId,
        data: {
          provider_key: selectedProvider,
          connection_id: selectedConnectionId,
          files: selectedCloudFiles
            .filter(file => !file.is_folder)
            .map(file => ({
              external_file_id: file.id,
              name: file.name,
              mime_type: file.mime_type,
              size: file.size,
              drive_id: file.drive_id,
              path: file.path,
              version: file.version
            }))
        }
      });

      toast.success('Cloud files imported successfully');
      closeCloudModal();
      refetch();
    } catch (err) {
      toast.error('Failed to import cloud files');
    }
  };

  const handleUploadAllFundsWithDuplicateCheck = e => {
    if (!e.target?.files) return;

    const validationError = validateUploadFiles(e.target.files);

    if (validationError) {
      toast.error(validationError, { position: 'top-center' });
      e.target.value = '';
      return;
    }

    const filesArray = Array.from(e.target.files);
    const existingFiles = getAllKnownFiles();

    if (existingFiles.length > 0) {
      const duplicates = detectDuplicateFiles(filesArray, existingFiles);
      if (duplicates.length > 0) {
        setDuplicateFiles(duplicates);
        setPendingUploadFiles({ files: filesArray, isAllFunds: true });
        if (e.target) e.target.value = '';
        return;
      }
    }

    trackUploadedFiles(filesArray);
    onUploadFilesForAllFunds(e);
  };

  const handleProceedWithDuplicates = selectedFiles => {
    if (!pendingUploadFiles) return;

    const { files: allPendingFiles, fundId, workflowId, isAllFunds } = pendingUploadFiles;
    const allDuplicateKeys = new Set(duplicateFiles.map(d => `${d.fileName}::${d.fileSize}`));
    const nonDuplicates = allPendingFiles.filter(
      f => !allDuplicateKeys.has(`${f.name}::${f.size}`)
    );
    const filesToUpload = [...nonDuplicates, ...selectedFiles];

    handleCloseDuplicateModal();

    if (filesToUpload.length > 0) {
      const syntheticEvent = { target: { files: filesToUpload, value: '' } };
      trackUploadedFiles(filesToUpload);
      if (isAllFunds) {
        onUploadFilesForAllFunds(syntheticEvent);
      } else {
        handleUploadFundFiles(syntheticEvent, fundId, workflowId);
      }
    }
  };

  const isCloudIntegrationEnabled = useFeatureFlag(
    FEATURE_FLAG_NAMES.FILE_UPLOAD_CLOUD_INTEGRATION
  );

  const onOpenFileUpload = () => {
    ref.current.click();
  };

  const navigateToDashboard = () => {
    navigate(`/project/quaters/${id}`);
  };

  const previousFundsAmount = fundMappings?.filter(fund => !!fund.previousFund)?.length || 0;
  const currentFundsAmount = fundMappings?.filter(fund => !!fund.currentFund)?.length || 0;
  const prevPeriod = quarters?.periods?.find(pr => pr.id === period?.previous_period_id);

  // preventing refresh page and data loss
  unstable_usePrompt({
    message: 'You have unsaved message. Are you sure want to proceed?',
    when: ({ currentLocation, nextLocation }) =>
      !isNextButtonClicked && hasChanges && currentLocation.pathname !== nextLocation.pathname
  });

  useEffect(() => {
    return () => {
      toast.dismiss();
    };
  }, []);

  const handleReset = () => {
    refetch();
  };

  const handleCloseWarning = () => {
    setIsNextButtonClicked(false);
    closeWarning();
  };

  const handleWarningConfirm = () => {
    if (isNextButtonClicked) {
      navigateToDashboard();
      return;
    }

    if (canManageWorkflow) {
      onSave();
    }
  };

  usePromptUnloadPage(hasChanges && !isLockedPeriod);

  useEffect(() => {
    agentStore.setCurrentScreen(WEB_PAGE_STATE_ENUMS.AutomationFileMapping);
  }, []);

  const { setLabels, clearLabels, setToolbarSlots } = useWorkflowChrome();

  useEffect(() => {
    // Source Files (automation) Next falls through to the stepper's default
    // `goToStage(nextStage)` — i.e. the next stepper stage (Extraction for
    // non-Conversion, etc.). Previously this overrode the handler to navigate
    // back to the Quarters dashboard, which dead-ended the user.
    setLabels({
      projectLabel: quarters?.name,
      periodLabel: period?.year && period?.period ? `${period.year}, ${period.period}` : undefined,
      workflowId: id,
      periodId,
      activeStageKey: 'source_files'
    });
    return () => clearLabels();
  }, [quarters?.name, period?.year, period?.period, id, periodId, setLabels, clearLabels]);

  // Keep a stable ref to the save handler so the toolbar-slot effect doesn't
  // re-fire each render (the handler from useMutateAutomationFiles gets a
  // new identity every render).
  const onSaveRef = useRef(onSave);
  useEffect(() => {
    onSaveRef.current = onSave;
  });

  useEffect(() => {
    // Save persists the user's per-file fund/sheet mappings — nothing to
    // persist on initial setup (the page just lists newly-uploaded files
    // with no prior period to map against), so hide the action entirely.
    if (isLockedPeriod || isInitialSetup) {
      setToolbarSlots(null);
      return undefined;
    }
    setToolbarSlots({
      saveAction: {
        label: 'Save',
        onClick: () => onSaveRef.current?.(),
        // Users without manage permission see the Save button as disabled
        // (matches the old inline PermissionGuard wrapper's behavior).
        disabled: isSaveMappingsLoading || !canManageWorkflow,
        loading: isSaveMappingsLoading,
        variant: 'primary'
      }
    });
    return () => setToolbarSlots(null);
  }, [isLockedPeriod, isInitialSetup, isSaveMappingsLoading, canManageWorkflow, setToolbarSlots]);

  const handleCloseDeleteModal = () => {
    setSelectedFileId(null);
    setSelectedFileName('');
  };

  return (
    <>
      <Wrapper>
        <input
          ref={ref}
          type="file"
          style={{ display: 'none' }}
          onChange={handleUploadAllFundsWithDuplicateCheck}
          multiple
          accept={acceptedFileTypes}
        />

        {/* The inline breadcrumb + Save / Next to Periods action row was
            removed when the workflow chrome (AppTopBar breadcrumb chips +
            WorkflowStepper + WorkflowToolbar) took over those roles. Save
            is registered as the toolbar's `saveAction` slot in the effect
            above; navigation flows through the stepper's Next arrow. */}

        <div className="container">
          {!isLockedPeriod && (
            <div className="top-header">
              <div className="warning-text">
                <WarningIcon />
                <p>Upload the files and reconcile them with the previous period</p>
              </div>

              {isCustomReportType ? (
                <Group gap="xs">
                  <DropZone
                    onDrop={files => handleUploadFundFilesWithDuplicateCheck({ target: { files } })}
                    variant="compact"
                    disabled={uploadFundFiles.isLoading || !canManageWorkflow}
                    disableClick
                    accept={acceptedDropzoneTypes}
                  >
                    <PermissionGuard allowed={canManageWorkflow}>
                      <Button
                        variant="outline"
                        onClick={handlePreventReset(
                          isCloudIntegrationEnabled ? openAddFilesModal : onOpenFileUpload
                        )}
                        disabled={uploadFundFiles.isLoading || !canManageWorkflow}
                        size="xs"
                        leftSection={<FiUpload />}
                      >
                        Add Files For All {fundsPluralLabel}
                      </Button>
                    </PermissionGuard>
                  </DropZone>
                </Group>
              ) : (
                <Group gap="xs">
                  <DropZone
                    onDrop={files => handleUploadAllFundsWithDuplicateCheck({ target: { files } })}
                    variant="compact"
                    disabled={uploadFundFiles.isLoading || !canManageWorkflow}
                    dragLabel={`Drop here to add files for all ${fundsPluralLabel}`}
                    disableClick
                    accept={acceptedDropzoneTypes}
                  >
                    <PermissionGuard allowed={canManageWorkflow}>
                      <Button
                        className="upload-file-btn"
                        variant="transparent"
                        onClick={handlePreventReset(
                          isCloudIntegrationEnabled ? openAddFilesModal : onOpenFileUpload
                        )}
                        disabled={uploadFundFiles.isLoading || !canManageWorkflow}
                        size="xs"
                        leftSection={<FiUpload />}
                      >
                        Add Files For All {fundsPluralLabel}
                      </Button>
                    </PermissionGuard>
                  </DropZone>
                </Group>
              )}
            </div>
          )}

          <TableHeader
            hasChanges={hasChanges}
            prevYear={prevPeriod?.year}
            prevPeriod={prevPeriod?.period}
            currentYear={period?.year}
            currentPeriod={period?.period}
            prevCount={previousFundsAmount}
            currentCount={currentFundsAmount}
            isExpandAll={isAllExpanded}
            isResetting={isFetching}
            toggleExpandAll={handleExpandAll}
            onReset={handleReset}
            reportType={quarters?.report_type}
            isInitialSetup={isInitialSetup}
          />

          <div
            className="table"
            style={{
              pointerEvents: isLoadingData ? 'none' : 'auto',
              opacity: isLoadingData ? 0.5 : 1
            }}
          >
            {fundMappings?.map((item, index) => (
              <FundCard
                key={'fund_' + index}
                index={index + 1}
                fileIndex={index}
                onChooseFile={onChooseFile}
                onChooseSheet={onChooseSheet}
                onChangeFundMappings={setFundMappings}
                currentFundId={item.currentFund?.id}
                currentFundName={item.currentFund?.name}
                previousFundName={item.previousFund?.name}
                bulkFiles={item.bulkFiles}
                perFundFiles={item.perFundFiles}
                handleDownloadFile={handleDownloadFile}
                handleAddMappedSheet={handleAddMappedSheet}
                handleRemoveMappedSheet={handleRemoveMappedSheet}
                handleDeleteFile={onDeleteFile}
                handleAutoMatchSheets={handleAutoMatchSheets}
                refetch={refetch}
                handlePreventReset={handlePreventReset}
                handleDeleteSheet={isInitialSetup ? handleDeletePeriodSheet : handleDeleteSheet}
                expandedFileIds={expandedFileIds}
                handleUnmapFile={handleUnmapFile}
                handleExpandFile={handleExpandFile}
                handleTrackChanges={handleTrackChanges}
                isInitialSetup={isInitialSetup}
                isCustomReportType={isCustomReportType}
                handleReplaceFile={handleReUploadFundFile}
                handleSelectSource={(fileId, sheetId, value) =>
                  handleSelectSource(item.currentFund?.id, fileId, sheetId, value)
                }
                hasInProgressFiles={hasInProgressFiles}
                handleUploadFundFiles={handleUploadFundFilesWithDuplicateCheck}
                onImportFromIntegration={openCloudModal}
                onOpenUploadFlow={isCloudIntegrationEnabled ? handlePerFundUploadFlow : undefined}
                acceptedFileTypes={acceptedFileTypes}
                acceptedDropzoneTypes={acceptedDropzoneTypes}
                canManageWorkflow={canManageWorkflow}
                sources={previousFilesAndSheets}
                previousPeriodId={period?.previous_period_id}
              />
            ))}
          </div>
        </div>
      </Wrapper>

      {!isLockedPeriod && (
        <>
          <SkipPasswordModal
            isOpen={isModalOpen}
            fileName={fileName}
            title={`${totalFiles} files are locked. Please enter the passwords to proceed with the upload.`}
            cancelLabel="Ignore this file"
            submitLabel={`Submit ${currentFileNumber}/${totalFiles}`}
            onCancel={onSkipFile}
            onConfirm={onUnblockSkipFile}
            isLoading={isLoading}
            isSkipping={isSkipping}
            isDeletingAllProtectedFiles={isDeletingAllProtectedFiles}
            onCancelAll={onSkipAllFiles}
          />

          <WarningPopup
            title="Unfinished Mapping"
            description={
              isNextButtonClicked
                ? 'You havenΓÇÖt completed file mapping yet. <br/>Are you sure you want to continue?'
                : 'You have unmapped data. <br/>Are you sure you want to continue?'
            }
            confirmLabel="Yes, Continue"
            isOpen={isOpenWarning}
            isLoading={isSaveMappingsLoading}
            onClose={handleCloseWarning}
            onConfirm={handleWarningConfirm}
          />

          <NewDeleteConfirmModal
            title="Delete File"
            description={`Are you sure you want to permanently delete ${selectedFileName}?`}
            isOpen={!!selectedFileId}
            onClose={handleCloseDeleteModal}
            submitLabel="Yes, Delete"
            cancelLabel="Cancel"
            type="delete"
            onConfirm={submitDeleteFile}
            isLoading={isDeletingFile}
            onDelete={submitDeleteFile}
          />

          <DuplicateFilesModal
            isOpen={duplicateFiles.length > 0}
            duplicates={duplicateFiles}
            onClose={handleCloseDuplicateModal}
            onProceed={handleProceedWithDuplicates}
            isLoading={uploadFundFiles.isLoading}
          />

          <AddFilesSourceModal
            opened={isAddFilesModalOpen}
            onClose={closeAddFilesModal}
            title={`Add Files For All ${fundsPluralLabel}`}
            description={`Choose how you want to add files for all ${fundsPluralLabel}.`}
            onUploadFromDevice={handleAddFilesFromLocal}
            onImportFromSource={handleAddFilesFromIntegration}
            isCloudIntegrationEnabled={isCloudIntegrationEnabled}
          />

          <CloudImportModal
            opened={isCloudModalOpen}
            onClose={closeCloudModal}
            title="Import files"
            providers={connectedProviders}
            previewProviders={providers}
            selectedProvider={selectedProvider}
            onSelectProvider={providerKey => {
              setSelectedProvider(providerKey);
              setSelectedConnectionId('');
              setSelectedCloudFiles([]);
              setCloudPath('');
              setCloudFolderId('');
              setCloudFolderIdTrail([]);
              setCloudFolderTrail([]);
              setCloudQuery('');
            }}
            connections={allConnections}
            hasAnyConnections={allConnections.length > 0}
            selectedProviderConnections={selectedProviderConnections}
            selectedConnectionId={selectedConnectionId}
            onSelectConnection={connectionId => {
              setSelectedConnectionId(connectionId);
              setSelectedCloudFiles([]);
              setCloudPath('');
              setCloudFolderId('');
              setCloudFolderIdTrail([]);
              setCloudFolderTrail([]);
              setCloudQuery('');
            }}
            searchValue={cloudQuery}
            onSearchChange={value => setCloudQuery(value)}
            onSearchSubmit={refetchCloudFiles}
            onRefreshConnections={handleRefreshConnections}
            onConnectProvider={handleConnectProvider}
            cloudPath={cloudPath}
            isCloudFolderNavigationEnabled={isCloudFolderNavigationEnabled}
            onNavigateBack={handleCloudBack}
            files={visibleCloudFiles}
            selectedCloudFiles={selectedCloudFiles}
            onFileClick={handleCloudItemClick}
            acceptedMimeTypes={new Set(Object.keys(acceptedDropzoneTypes))}
            isFetchingConnections={isFetchingConnections}
            isFetchingCloudFiles={isFetchingCloudFiles}
            onImport={handleImportCloudFiles}
            isImporting={importCloudFiles.isLoading}
            onShowMoreIntegrations={() => navigate('/integrations')}
          />
        </>
      )}
    </>
  );
}
